import { useCallback, useEffect } from 'react';
import { useInterval } from 'usehooks-ts';
import type { TimelinePlayback } from 'features/mapping/mapping.types';
import { usePlaybackState } from 'hooks/usePlaybackState';
import { buildTimeRangeFrames, TimeRange } from './useTimeRangePlayback.utils';

type UseTimeRangePlaybackOptions = TimeRange & {
  visible: boolean;
  /** Seconds for one full loop through the frames */
  loopSec: number;
};

const FRAME_REBUILD_INTERVAL_MS = 3 * 60 * 1000;

/**
 * Playback over a range relative to now, for layers not driven by a provider
 * like Terrier.
 */
export const useTimeRangePlayback = ({
  visible,
  startOffsetMin,
  endOffsetMin,
  stepMin,
  loopSec,
}: UseTimeRangePlaybackOptions): TimelinePlayback => {
  const {
    frames,
    currentFrameIndex,
    isPlaying,
    isLoading,
    setFrames,
    setIsPlaying,
    jumpToFrame,
    stopAndReset,
  } = usePlaybackState();
  const lastIndex = frames.length - 1;

  const buildFrames = useCallback(
    () =>
      buildTimeRangeFrames(
        { startOffsetMin, endOffsetMin, stepMin },
        Date.now(),
      ),
    [startOffsetMin, endOffsetMin, stepMin],
  );

  // Enter paused on the last frame; clear on exit so re-entering never shows a
  // stale range
  useEffect(() => {
    if (!visible) {
      setFrames([]);
      stopAndReset(0);
      return;
    }
    const nextFrames = buildFrames();
    setFrames(nextFrames);
    jumpToFrame(nextFrames.length - 1);
  }, [visible, buildFrames, setFrames, jumpToFrame, stopAndReset]);

  // Keep the range anchored to now; the index keeps its relative time
  useInterval(
    () => setFrames(buildFrames()),
    visible ? FRAME_REBUILD_INTERVAL_MS : null,
  );

  useInterval(
    () => jumpToFrame((currentFrameIndex + 1) % frames.length),
    visible && isPlaying && frames.length > 0
      ? (loopSec * 1000) / frames.length
      : null,
  );

  const onFrameChange = (index: number): void => {
    if (frames.length === 0) return;
    jumpToFrame(Math.max(0, Math.min(index, lastIndex)), true);
  };

  const onPlayPause = (): void => {
    if (frames.length === 0) return;
    // Play from the end restarts the loop, like radar
    if (!isPlaying && currentFrameIndex >= lastIndex) jumpToFrame(0);
    setIsPlaying(!isPlaying);
  };

  return {
    isLoading,
    frames,
    currentFrameIndex,
    isPlaying,
    onFrameChange,
    onPlayPause,
  };
};
